// Tour rings in a panorama: a disc on the floor under each neighbouring station,
// seen from the current one.

export const TOUR_RING_WORLD_RADIUS = 0.6;

export interface RingShape { yaw: number; pitch: number; w: number; v: number }

/**
 * Projected half-extents of a floor ring. A flat disc seen from an eye
 * `eyeHeight` above its plane is a wide, thin ellipse, not a circle: its height
 * on screen is the pitch difference between its near and far edge. Treating it
 * as a circle overstated that 4–10×, so a row of stations down a corridor — thin
 * ellipses stacked towards the horizon, none touching — read as overlapping and
 * the farther rings were dropped.
 */
export function ringExtent(horizontalDist: number, eyeHeight: number, radius = TOUR_RING_WORLD_RADIUS): { w: number; v: number } {
  const d = Math.max(horizontalDist, 0.01);
  const h = Math.abs(eyeHeight);
  const w = Math.atan(radius / d);
  const v = d > radius ? (Math.atan2(h, d - radius) - Math.atan2(h, d + radius)) / 2 : Math.PI / 4;
  return { w, v: Math.max(v, 1e-3) };
}

/** True when two rings overlap on screen enough to read as one (the nearer is kept). */
export function ringsOverlap(a: RingShape, b: RingShape, threshold = 0.7): boolean {
  let dYaw = Math.abs(a.yaw - b.yaw);
  if (dYaw > Math.PI) dYaw = 2 * Math.PI - dYaw;
  dYaw *= Math.cos((a.pitch + b.pitch) / 2);
  const dPitch = Math.abs(a.pitch - b.pitch);
  const x = dYaw / (a.w + b.w);
  const y = dPitch / (a.v + b.v);
  return x * x + y * y < threshold * threshold;
}
